// The ONE download button for every page, model, and mode.
//
// - Always WHITE.
// - Always a menu: opening it lists every file type available for the current
//   model, and the user picks one every time (no default / split-button that
//   downloads a "last format" on a bare click).
// - Content comes entirely from the download registry (HomepageContext) — the
//   active viewer registers its `DownloadFormat[]`; this component renders it.
//   Nothing registered ⇒ renders null (so the feature-tree footer's
//   `empty:hidden` keeps it invisible until there's something to download).
//
// This is the single sanctioned download UI. Do NOT add another download button
// anywhere — register formats into the registry instead. `npm run lint:download`
// (scripts/check-download.mjs) fails CI if a second one appears.

import { useState } from 'react';
import { Download, ChevronUp, Loader2 } from '@/components/ui/icons';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useHomepageOptional } from '@/contexts/homepage-context';
import type { DownloadFormat } from './DownloadTypes';

// The one white treatment shared by every download button, everywhere.
const WHITE_BTN = 'bg-white text-black hover:bg-white/90 hover:text-black';

export interface UniversalDownloadMenuProps {
  /** Wrapper class (e.g. spacing when floated vs. inline in the footer). */
  className?: string;
  /**
   * Explicit formats for a SCOPED menu (e.g. a result panel offering its own
   * artifact). The global registry is one last-write-wins slot — a panel that
   * rendered the registry here could silently serve ANOTHER feature's file once
   * something else registered after it. Pass the panel's own formats instead;
   * omitted ⇒ the registry-backed global menu (the footer slot).
   */
  formats?: DownloadFormat[] | null;
}

export function UniversalDownloadMenu({
  className,
  formats: scopedFormats,
}: UniversalDownloadMenuProps) {
  const registryFormats = useHomepageOptional()?.downloadFormats ?? null;
  const formats = scopedFormats !== undefined ? scopedFormats : registryFormats;
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Nothing to download yet → render nothing at all (no DOM node), so the
  // footer slot's `empty:hidden` collapses cleanly.
  if (!formats || formats.length === 0) return null;

  const runFormat = async (format: DownloadFormat) => {
    if (format.disabled || busyId) return;
    try {
      setBusyId(format.id);
      await format.run();
    } finally {
      setBusyId(null);
      setOpen(false);
    }
  };

  return (
    <div className={cn('flex w-full', className)}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label="download model"
            className={cn(
              'flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5',
              WHITE_BTN,
            )}
          >
            {busyId ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span>Download</span>
            <ChevronUp className="ml-1 h-3 w-3 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          {formats.map((format) => (
            <DropdownMenuItem
              key={format.id}
              disabled={format.disabled || busyId !== null}
              onClick={(e) => {
                e.preventDefault();
                void runFormat(format);
              }}
              className="flex cursor-pointer items-center text-telekinesis-text-primary"
            >
              {busyId === format.id ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              <span className="text-sm">{format.label}</span>
              {format.hint ? (
                <span className="ml-3 text-xs text-telekinesis-text-primary/60">
                  {busyId === format.id
                    ? 'Downloading…'
                    : format.disabled
                      ? 'Unavailable'
                      : format.hint}
                </span>
              ) : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
